import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { API } from "@/App";
import { 
  TrendUp, 
  TrendDown,
  ArrowRight,
  Lightning,
  Eye,
  Target,
  Clock,
  ArrowClockwise,
  CurrencyBtc,
  CurrencyDollar,
  ChartLineUp,
  Coins,
  MagnifyingGlass,
  CheckCircle,
  Warning,
  XCircle
} from "@phosphor-icons/react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";

// Price Comparison Widget
const PriceCompareWidget = ({ data }) => {
  if (!data || data.length === 0) return null;

  const statusIcon = { sync: CheckCircle, warning: Warning, error: XCircle };
  const statusColor = { sync: "#10b981", warning: "#f59e0b", error: "#ef4444" };
  const statusLabel = { sync: "Synchro", warning: "Ecart", error: "Decale" };

  return (
    <div className="mb-4 p-3 bg-slate-900 rounded-lg" data-testid="price-compare-widget">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Comparaison temps reel</span>
        <span className="text-[10px] text-slate-500">vs Coinbase</span>
      </div>
      <div className="flex gap-2 overflow-x-auto">
        {data.map((item) => {
          const Icon = statusIcon[item.status] || CheckCircle;
          const color = statusColor[item.status] || "#10b981";
          return (
            <div key={item.symbol} className="flex-1 min-w-[140px] bg-slate-800 rounded-lg px-3 py-2" data-testid={`compare-${item.symbol}`}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-bold text-white">{item.symbol}</span>
                <Icon size={14} weight="fill" style={{ color }} />
              </div>
              <div className="text-sm font-mono text-white font-bold">${item.our_price?.toLocaleString()}</div>
              <div className="flex items-center justify-between mt-1">
                <span className="text-[10px] text-slate-400">Ref: ${item.ref_price?.toLocaleString()}</span>
                <span className="text-[10px] font-mono font-bold" style={{ color }}>
                  {item.delta_pct >= 0 ? '+' : ''}{item.delta_pct?.toFixed(3)}%
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Category tabs config
const CATEGORIES = [
  { id: "all", label: "Tous", icon: Eye },
  { id: "crypto", label: "Crypto", icon: CurrencyBtc },
  { id: "precious_metal", label: "Or & Argent", icon: Coins },
  { id: "index", label: "Indices", icon: ChartLineUp },
];

// Score Badge Component
const ScoreBadge = ({ score }) => {
  let className = "score-badge ";
  if (score >= 90) className += "score-exceptional";
  else if (score >= 85) className += "score-good";
  else if (score >= 70) className += "score-average";
  else className += "score-poor";
  
  return (
    <span className={className} data-testid="score-badge">
      {score}
    </span>
  );
};

// Timing Badge Component
const TimingBadge = ({ timing }) => {
  const config = {
    enter_now: { label: "ENTRER", className: "timing-enter-now" },
    wait_confirmation: { label: "ATTENDRE", className: "timing-wait" },
    too_late: { label: "TROP TARD", className: "timing-too-late" }
  };
  
  const { label, className } = config[timing] || config.wait_confirmation;
  
  return (
    <span className={`score-badge ${className}`} data-testid="timing-badge">
      {label}
    </span>
  );
};

// Mini Sparkline Chart
const MiniSparkline = ({ data, trend }) => {
  if (!data || data.length === 0) return null;
  
  const chartData = data.slice(-20).map((item, index) => ({
    index,
    price: item.price
  }));
  
  const color = trend === "bullish" ? "#16a34a" : trend === "bearish" ? "#dc2626" : "#64748b";
  
  return (
    <div className="h-10 w-24">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={chartData}>
          <Line 
            type="linear" 
            dataKey="price" 
            stroke={color} 
            strokeWidth={1.5}
            dot={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

// Opportunity Card Component
const OpportunityCard = ({ opportunity, onClick }) => {
  const {
    symbol,
    name,
    current_price,
    price_change_24h,
    score,
    trend,
    opportunity_type,
    timing,
    risk_reward,
    price_history
  } = opportunity;

  const typeLabels = {
    breakout: "Breakout",
    reversal: "Reversal",
    trend_continuation: "Continuation"
  };

  return (
    <div 
      className="opportunity-card"
      onClick={onClick}
      data-testid={`opportunity-card-${symbol}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="flex items-center gap-2">
            <span className="font-heading font-bold text-slate-900">{symbol}</span>
            <Badge variant="outline" className="text-xs font-data">
              {typeLabels[opportunity_type] || opportunity_type}
            </Badge>
          </div>
          <p className="text-xs text-slate-500 font-body mt-0.5">{name}</p>
        </div>
        <ScoreBadge score={score} />
      </div>
      
      <div className="flex items-center justify-between mb-3">
        <div>
          <p className="font-data text-lg font-medium text-slate-900 tabular-nums">
            ${current_price?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 6 })}
          </p>
          <p className={`font-data text-xs tabular-nums ${price_change_24h >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
            {price_change_24h >= 0 ? '+' : ''}{price_change_24h?.toFixed(2)}%
          </p>
        </div>
        <MiniSparkline data={price_history} trend={trend} />
      </div>
      
      <div className="flex items-center justify-between pt-3 border-t border-slate-100">
        <div className="flex items-center gap-2">
          {trend === "bullish" ? (
            <TrendUp size={16} className="text-emerald-600" weight="bold" />
          ) : trend === "bearish" ? (
            <TrendDown size={16} className="text-red-600" weight="bold" />
          ) : (
            <span className="w-4 h-0.5 bg-slate-400 rounded" />
          )}
          <span className="text-xs text-slate-500 font-body">R:R {risk_reward}x</span>
        </div>
        <TimingBadge timing={timing} />
      </div>
    </div>
  );
};

// Market Overview Card
const MarketOverview = ({ data, loading, onMoverClick }) => {
  if (loading) {
    return (
      <Card className="border-slate-200 rounded-sm shadow-none">
        <CardHeader className="pb-2">
          <CardTitle className="font-heading text-sm font-bold uppercase tracking-wider text-slate-500">
            Aperçu du Marché
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-6 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-slate-200 rounded-sm shadow-none" data-testid="market-overview">
      <CardHeader className="pb-2">
        <CardTitle className="font-heading text-sm font-bold uppercase tracking-wider text-slate-500">
          Aperçu du Marché
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <span className="text-sm text-slate-600 font-body">Sentiment</span>
            <Badge 
              className={`font-data ${data?.market_sentiment === 'bullish' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}
            >
              {data?.market_sentiment === 'bullish' ? 'HAUSSIER' : 'BAISSIER'}
            </Badge>
          </div>
          
          <div className="flex justify-between items-center">
            <span className="text-sm text-slate-600 font-body">Variation 24h</span>
            <span className={`font-data text-sm tabular-nums ${(data?.average_change_24h || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
              {(data?.average_change_24h || 0) >= 0 ? '+' : ''}{data?.average_change_24h?.toFixed(2)}%
            </span>
          </div>
          
          <div className="flex justify-between items-center">
            <span className="text-sm text-slate-600 font-body">Haussiers</span>
            <span className="font-data text-sm text-emerald-600 tabular-nums">{data?.bullish_count || 0}</span>
          </div>
          
          <div className="flex justify-between items-center">
            <span className="text-sm text-slate-600 font-body">Baissiers</span>
            <span className="font-data text-sm text-red-600 tabular-nums">{data?.bearish_count || 0}</span>
          </div>
        </div>
        
        {data?.top_movers?.length > 0 && (
          <div className="mt-4 pt-4 border-t border-slate-100">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">Top Movers</p>
            <div className="space-y-2">
              {data.top_movers.slice(0, 5).map((mover) => (
                <button
                  key={mover.symbol}
                  onClick={() => onMoverClick(mover.symbol)}
                  className="w-full flex justify-between items-center p-2 -mx-2 rounded-sm hover:bg-slate-50 transition-colors cursor-pointer group"
                  data-testid={`top-mover-${mover.symbol}`}
                >
                  <div className="flex items-center gap-2">
                    <span className="font-heading text-sm font-medium text-slate-900 group-hover:text-blue-600 transition-colors">
                      {mover.symbol}
                    </span>
                    <ArrowRight size={12} className="text-slate-400 group-hover:text-blue-600 transition-colors" />
                  </div>
                  <span className={`font-data text-xs tabular-nums ${mover.change >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                    {mover.change >= 0 ? '+' : ''}{mover.change?.toFixed(2)}%
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Stats Card
const StatsCard = ({ icon: Icon, label, value, subValue, trend, onClick, active }) => (
  <Card className={`rounded-sm shadow-none transition-all cursor-pointer hover:shadow-md hover:border-blue-300 ${active ? 'border-blue-500 bg-blue-50/50 ring-1 ring-blue-200' : 'border-slate-200'}`}
    onClick={onClick}
    data-testid={`stat-card-${label.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
  >
    <CardContent className="p-4">
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-sm ${active ? 'bg-blue-100' : 'bg-slate-100'}`}>
          <Icon size={18} className={active ? 'text-blue-600' : 'text-slate-600'} weight="regular" />
        </div>
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 font-body">{label}</p>
          <p className="font-data text-xl font-medium text-slate-900 tabular-nums">{value}</p>
          {subValue && (
            <p className={`font-data text-xs tabular-nums ${trend === 'up' ? 'text-emerald-600' : trend === 'down' ? 'text-red-600' : 'text-slate-500'}`}>
              {subValue}
            </p>
          )}
        </div>
      </div>
    </CardContent>
  </Card>
);

export default function Dashboard() {
  const navigate = useNavigate();
  const [opportunities, setOpportunities] = useState([]);
  const [marketOverview, setMarketOverview] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [liveConnected, setLiveConnected] = useState(false);
  const [scoreFilter, setScoreFilter] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [priceCompare, setPriceCompare] = useState([]);

  const fetchData = useCallback(async (showRefresh = false, category = "all") => {
    if (showRefresh) setRefreshing(true);
    try {
      const categoryParam = category !== "all" ? `&category=${category}` : "";
      const [oppsResponse, marketResponse, statsResponse] = await Promise.all([
        fetch(`${API}/opportunities?min_score=0&limit=100${categoryParam}`),
        fetch(`${API}/market-overview`),
        fetch(`${API}/stats`)
      ]);
      
      const [oppsData, marketData, statsData] = await Promise.all([
        oppsResponse.json(),
        marketResponse.json(),
        statsResponse.json()
      ]);
      
      setOpportunities(oppsData);
      setMarketOverview(marketData);
      setStats(statsData);
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  // SSE real-time connection - delayed to not block initial load
  useEffect(() => {
    let eventSource;
    const timer = setTimeout(() => {
      try {
        eventSource = new EventSource(`${API}/stream`);
        eventSource.onopen = () => setLiveConnected(true);
        eventSource.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            if (data.type === 'update' && data.top_movers) {
              setMarketOverview(prev => prev ? { ...prev, top_movers: data.top_movers } : prev);
            }
          } catch (e) { /* ignore */ }
        };
        eventSource.onerror = () => { 
          setLiveConnected(false);
          if (eventSource) eventSource.close();
        };
      } catch (e) { /* SSE not supported */ }
    }, 10000); // Wait 10s before connecting SSE
    return () => { 
      clearTimeout(timer);
      if (eventSource) eventSource.close(); 
    };
  }, []);

  useEffect(() => {
    fetchData(false, selectedCategory);
    // Full data refresh every 60s
    const interval = setInterval(() => fetchData(false, selectedCategory), 60000);
    return () => clearInterval(interval);
  }, [fetchData, selectedCategory]);

  // Live price refresh every 30s
  useEffect(() => {
    const refreshPrices = async () => {
      try {
        const res = await fetch(`${API}/prices/live`);
        if (!res.ok) return;
        const livePrices = await res.json();
        setOpportunities(prev => prev.map(opp => {
          const liveData = livePrices[opp.symbol];
          if (liveData && liveData.price > 0) {
            return { ...opp, current_price: liveData.price, price_change_24h: liveData.change_24h };
          }
          return opp;
        }));
      } catch (e) { /* silent */ }
    };
    const refreshCompare = async () => {
      try {
        const res = await fetch(`${API}/prices/compare`);
        if (res.ok) setPriceCompare(await res.json());
      } catch (e) { /* silent */ }
    };
    refreshCompare();
    const priceInterval = setInterval(refreshPrices, 30000);
    const compareInterval = setInterval(refreshCompare, 30000);
    return () => { clearInterval(priceInterval); clearInterval(compareInterval); };
  }, []);

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    setLoading(true);
    fetchData(false, category);
  };

  const premiumOpportunities = opportunities.filter(o => o.score >= 90);
  const strongOpportunities = opportunities.filter(o => o.score >= 85 && o.score < 90);
  
  // Apply score filter from stats cards
  const filteredOpportunities = scoreFilter 
    ? opportunities.filter(o => o.score >= scoreFilter)
    : opportunities;
  
  // Apply search filter
  const searchFiltered = searchQuery.trim() 
    ? filteredOpportunities.filter(o => 
        o.symbol.toLowerCase().includes(searchQuery.toLowerCase()) ||
        o.name.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : filteredOpportunities;

  return (
    <div data-testid="dashboard-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Dashboard</h1>
          <div className="flex items-center gap-2 mt-1">
            <p className="text-sm text-slate-500 font-body">
              Opportunites filtrees en temps reel
            </p>
            {liveConnected && (
              <span className="flex items-center gap-1 text-xs text-emerald-600">
                <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" />
                Live
              </span>
            )}
          </div>
        </div>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={() => fetchData(true, selectedCategory)}
          disabled={refreshing}
          className="rounded-sm"
          data-testid="refresh-button"
        >
          <ArrowClockwise size={16} className={refreshing ? 'animate-spin' : ''} />
          <span className="ml-2">Actualiser</span>
        </Button>
      </div>

      {/* Price Comparison Widget */}
      <PriceCompareWidget data={priceCompare} />

      {/* Search bar - prominent */}
      <div className="relative mb-4" data-testid="search-bar-wrapper">
        <MagnifyingGlass size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input
          type="text"
          placeholder="Rechercher par symbole ou nom (ex: BTC, Ethereum, Solana...)"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="rounded-sm pl-10 h-11 w-full font-body text-sm border-slate-300 focus:border-slate-500"
          data-testid="search-input"
        />
        {searchQuery && (
          <button 
            onClick={() => setSearchQuery("")} 
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
            data-testid="search-clear"
          >
            <span className="text-xs font-body">Effacer</span>
          </button>
        )}
      </div>

      {/* Category Filter Tabs */}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
        {CATEGORIES.map((cat) => (
          <Button
            key={cat.id}
            variant={selectedCategory === cat.id ? "default" : "outline"}
            size="sm"
            onClick={() => handleCategoryChange(cat.id)}
            className="rounded-sm whitespace-nowrap"
            data-testid={`filter-${cat.id}`}
          >
            <cat.icon size={16} className="mr-2" />
            {cat.label}
          </Button>
        ))}
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <StatsCard 
          icon={Lightning} 
          label="Premium (90+)" 
          value={premiumOpportunities.length}
          subValue={premiumOpportunities.length > 0 ? "Action recommandée" : "Aucune actuellement"}
          trend={premiumOpportunities.length > 0 ? 'up' : 'neutral'}
          onClick={() => setScoreFilter(scoreFilter === 90 ? null : 90)}
          active={scoreFilter === 90}
        />
        <StatsCard 
          icon={Target} 
          label="Fortes (85+)" 
          value={strongOpportunities.length}
          onClick={() => setScoreFilter(scoreFilter === 85 ? null : 85)}
          active={scoreFilter === 85}
        />
        <StatsCard 
          icon={Eye} 
          label="Total Analysé" 
          value={opportunities.length}
          onClick={() => setScoreFilter(null)}
          active={scoreFilter === null}
        />
        <StatsCard 
          icon={Clock} 
          label="Win Rate" 
          value={stats?.win_rate ? `${stats.win_rate}%` : "—"}
          subValue={stats?.total_trades > 0 ? `${stats.total_trades} trades` : "Pas de données"}
          onClick={() => navigate('/history')}
        />
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        {/* Opportunities List */}
        <div className="lg:col-span-8">
          <Card className="border-slate-200 rounded-sm shadow-none">
            <CardHeader className="pb-2 flex flex-row items-center justify-between">
              <CardTitle className="font-heading text-sm font-bold uppercase tracking-wider text-slate-500">
                {searchQuery ? `Resultats pour "${searchQuery}"` :
                 scoreFilter === 90 ? "Opportunités Premium (90+)" :
                 scoreFilter === 85 ? "Opportunités Fortes (85+)" :
                 selectedCategory === "all" ? "Top 20 Opportunités" : 
                 selectedCategory === "crypto" ? "Top Crypto" :
                 selectedCategory === "precious_metal" ? "Or & Argent" :
                 "Indices"} (triées par score)
              </CardTitle>
              <Badge variant="outline" className="font-data text-xs">
                {searchFiltered.length} résultat{searchFiltered.length !== 1 ? 's' : ''}
              </Badge>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="space-y-4">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Skeleton key={i} className="h-28 w-full" />
                  ))}
                </div>
              ) : searchFiltered.length === 0 ? (
                <div className="text-center py-12">
                  <Eye size={48} className="mx-auto text-slate-300 mb-4" />
                  <p className="text-slate-500 font-body">
                    {searchQuery ? `Aucun resultat pour "${searchQuery}"` : "Aucune opportunité qualifiée"}
                  </p>
                  <p className="text-sm text-slate-400 font-body mt-1">
                    {searchQuery ? "Essayez un autre terme de recherche" : "Les marchés sont analysés en continu"}
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {searchFiltered.map((opp, index) => (
                    <OpportunityCard 
                      key={opp.id}
                      opportunity={opp}
                      onClick={() => navigate(`/opportunity/${opp.symbol}`)}
                    />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="lg:col-span-4 space-y-4">
          <MarketOverview 
            data={marketOverview} 
            loading={loading} 
            onMoverClick={(symbol) => navigate(`/opportunity/${symbol}`)}
          />
          
          {/* Quick Actions */}
          <Card className="border-slate-200 rounded-sm shadow-none">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading text-sm font-bold uppercase tracking-wider text-slate-500">
                Actions Rapides
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Button 
                variant="outline" 
                className="w-full justify-between rounded-sm"
                onClick={() => navigate('/history')}
                data-testid="view-history-btn"
              >
                <span className="font-body">Voir l'historique</span>
                <ArrowRight size={16} />
              </Button>
              <Button 
                variant="outline" 
                className="w-full justify-between rounded-sm"
                onClick={() => navigate('/settings')}
                data-testid="view-settings-btn"
              >
                <span className="font-body">Paramètres</span>
                <ArrowRight size={16} />
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
